import { select } from 'd3-selection';

const DEFAULT_TITLE = 'SkewT Diagram Guide';

const DEFAULT_TERMS = [
    { term: 'CAPE (red area)', def: 'Energy available for thunderstorm development - larger areas create more powerful storms' },
    { term: 'CIN (blue area)', def: 'Stable layer preventing thunderstorms from forming' },
    { term: 'LCL', def: 'Height where cumulus clouds start to condense (cloudbase)' },
    { term: 'LFC', def: 'Height where thermals become buoyant and rise freely' },
    { term: 'EL', def: 'Equilibrium level of a rising parcel - maximum thunderstorm height' },
    { term: 'Red line', def: 'Temperature profile' },
    { term: 'Green line', def: 'Dew point profile' },
    { term: 'Blue line', def: 'Wind strength at each height' },
    { term: 'Pink path', def: 'Virtual parcel path - theoretical temperature of rising air assuming no mixing with environment' },
    { term: 'Grey areas', def: 'Where clouds are likely to form' },
];

const POPOVER_W = 400;
const POPOVER_H = 420;
const WRAP_CHARS = 32;

function wrap(text, max) {
    const words = text.split(' ');
    const lines = [];
    let current = '';
    for (const w of words) {
        const candidate = current ? current + ' ' + w : w;
        if (candidate.length > max && current) {
            lines.push(current);
            current = w;
        } else {
            current = candidate;
        }
    }
    if (current) lines.push(current);
    return lines;
}

/**
 * Render a "?" help button (top-left corner of the chart) and a legend popover
 * that toggles on click. Mirrors Skysight's helpButton/helpPopover behavior
 * decoded from `docs/skysight-skewt-bundle.js`.
 *
 * Click outside the button or popover closes it.
 *
 * @param {import('d3-selection').Selection} container - the chart's inner <g>
 * @param {{ title?: string, terms?: Array<{term: string, def: string}> }} [i18n]
 *   Localized strings. Falls back to English defaults when not provided so the
 *   lib has no react-i18next dependency.
 */
export function drawHelpIcon(container, i18n = {}) {
    const title = i18n.title || DEFAULT_TITLE;
    const terms = Array.isArray(i18n.terms) && i18n.terms.length > 0 ? i18n.terms : DEFAULT_TERMS;

    container.selectAll('.skewt-ext-help-icon').remove();
    container.selectAll('.skewt-ext-help-popover').remove();

    const button = container.append('g')
        .attr('class', 'skewt-ext-help-icon')
        .attr('transform', 'translate(-20, -20)')
        .style('cursor', 'pointer');

    const circle = button.append('circle')
        .attr('cx', 0)
        .attr('cy', 0)
        .attr('r', 10)
        .attr('fill', '#4a5568')
        .attr('stroke', '#2d3748')
        .attr('stroke-width', 1);

    button.append('text')
        .attr('x', 0)
        .attr('y', 4)
        .attr('text-anchor', 'middle')
        .attr('font-size', '13px')
        .attr('font-weight', 'bold')
        .attr('fill', 'white')
        .attr('pointer-events', 'none')
        .text('?');

    const popover = container.append('g')
        .attr('class', 'skewt-ext-help-popover')
        .attr('transform', 'translate(0, 0)')
        .style('display', 'none');

    popover.append('rect')
        .attr('width', POPOVER_W)
        .attr('height', POPOVER_H)
        .attr('rx', 6)
        .attr('fill', 'white')
        .attr('stroke', '#2d3748')
        .attr('stroke-width', 1)
        .style('filter', 'drop-shadow(0 4px 6px rgba(0,0,0,0.15))');

    const inner = popover.append('g').attr('transform', 'translate(14, 26)');

    inner.append('text')
        .attr('x', 0)
        .attr('y', 0)
        .attr('font-size', '14px')
        .attr('font-weight', 'bold')
        .attr('fill', '#1a202c')
        .text(title);

    const termX = 0;
    const defX = 145;
    let rowY = 22;

    for (const { term, def } of terms) {
        inner.append('text')
            .attr('x', termX)
            .attr('y', rowY)
            .attr('font-size', '11px')
            .attr('font-weight', 'bold')
            .attr('fill', '#2d3748')
            .text(term + ':');

        const defText = inner.append('text')
            .attr('x', defX)
            .attr('y', rowY)
            .attr('font-size', '10px')
            .attr('fill', '#4a5568');

        const lines = wrap(def, WRAP_CHARS);
        lines.forEach((line, i) => {
            const tspan = defText.append('tspan').text(line);
            if (i === 0) {
                tspan.attr('x', defX).attr('y', rowY);
            } else {
                tspan.attr('x', defX).attr('dy', '12');
            }
        });

        rowY += Math.max(14 + lines.length * 12, 26);
    }

    let isOpen = false;
    const setOpen = (open) => {
        isOpen = open;
        popover.style('display', open ? 'block' : 'none');
        circle.attr('fill', open ? '#2b6cb0' : '#4a5568');
    };

    button.on('click', (event) => {
        event.stopPropagation();
        setOpen(!isOpen);
    });

    popover.on('click', (event) => {
        event.stopPropagation();
    });

    const svgNode = container.node()?.ownerSVGElement;
    if (svgNode) {
        const svg = select(svgNode);
        svg.on('click.skewt-ext-help', null);
        svg.on('click.skewt-ext-help', () => {
            if (isOpen) setOpen(false);
        });
    }
}
